"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { ArrowLeft, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { MultiChoice, SingleChoice, YNU } from "./choice"
import {
  DATA_TYPES, DEMANDS, GEOGRAPHY, HELP_OPTIONS, INCIDENT_TYPES, ORG_TYPES, PAID_OPTIONS,
  emptyIntake, geographyOf, helpOf, questionCopy, shown, withAnswer, withGeography, withHelp,
  type AnswerKey, type Demand, type GeographyId, type IncidentTypeId, type Intake,
} from "@/lib/breach/intake"
import { addLog, newWorkspace } from "@/lib/breach/workspace"
import { saveWorkspace } from "@/lib/breach/storage"
import { WIZARD_SCREENS, screensFor, type ScreenId } from "@/lib/breach/wizard-screens"
import { SaveProgress } from "./save-progress"
import { ClearAll } from "./clear-all"
import { clearDraft, decodeShare, encodeShare, loadDraft, saveDraft, screenIndex, shareFromHash } from "@/lib/breach/intake-draft"

const nowLocal = () => {
  const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}
const toIso = (local: string) => (local ? new Date(local).toISOString() : "")

const FIELD_FOR: Partial<Record<ScreenId, AnswerKey>> = {
  active: "stillActive", access: "unauthorizedAccess", taken: "dataTaken", encrypted: "encrypted", demand: "demand", paid: "paid",
}

export function IntakeWizard() {
  const router = useRouter()
  const params = useSearchParams()
  const [i, setI] = useState<Intake>(() => emptyIntake())
  const [noticed, setNoticed] = useState("")
  const [confirmed, setConfirmed] = useState("")
  const [error, setError] = useState("")
  const [ready, setReady] = useState(false)
  const [note, setNote] = useState("")
  const [savedAt, setSavedAt] = useState<string | undefined>(undefined)

  // Resume: a share link in the #fragment wins, otherwise the saved draft. The fragment is removed from
  // the address bar straight away so the answers are not left in the URL, history, or error reports.
  const resumed = useRef(false)
  useEffect(() => {
    // Run once: React may invoke effects twice in development, and the second run would no longer see the
    // fragment (we remove it) and would wrongly reload the old draft over the shared answers.
    if (resumed.current) return
    resumed.current = true
    const frag = shareFromHash(window.location.hash)
    let d = frag ? decodeShare(frag) : null
    if (frag) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search)
      if (!d) setNote("That link couldn't be read, so we started fresh.")
    }
    if (!d) d = loadDraft()
    if (d) { setI(d.intake); setNoticed(d.noticed); setConfirmed(d.confirmed) }
    setReady(true)
  }, [])

  // Keep the draft up to date so Back, refresh, and a bookmarked step all resume.
  useEffect(() => {
    if (ready) { saveDraft({ intake: i, noticed, confirmed }); setSavedAt(new Date().toISOString()) }
  }, [ready, i, noticed, confirmed])

  // Move focus to the new question's heading so keyboard and screen-reader users land in the right place.
  const headingRef = useRef<HTMLHeadingElement>(null)
  const firstRender = useRef(true)
  const screenKey = screensFor(i)[screenIndex(params.get("q"), screensFor(i))]
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return }
    headingRef.current?.focus()
  }, [screenKey])

  const set = <K extends keyof Intake>(k: K, v: Intake[K]) => setI((p) => ({ ...p, [k]: v }))
  const answer = <K extends AnswerKey>(k: K, v: Intake[K]) => setI((p) => withAnswer(p, k, v))

  const copy = questionCopy(i.incidentType)
  const screens = screensFor(i)
  // The step lives in the URL (?q=) so Back works and a step can be bookmarked. Answers never go in the query string.
  const idx = screenIndex(params.get("q"), screens)
  const screen = screens[idx]
  const last = idx >= screens.length - 1
  const pct = Math.round(((idx + 1) / screens.length) * 100)
  const go = (n: number) => router.push(`/start?q=${screens[Math.max(0, Math.min(n, screens.length - 1))]}`, { scroll: false })
  const copyLink = async () => {
    const link = `${window.location.origin}/start?q=${screen}#a=${encodeShare({ intake: i, noticed, confirmed })}`
    try { await navigator.clipboard.writeText(link); setNote("Link copied. It contains your answers, so only share it with people you trust.") }
    catch { setNote("Couldn't copy the link in this browser.") }
  }

  const startOver = () => {
    if (!confirm("Clear the answers you've entered on this device and start again?")) return
    clearDraft(); setI(emptyIntake()); setNoticed(""); setConfirmed(""); setNote("")
    router.push("/start", { scroll: false })
  }

  const titles: Record<ScreenId, string> = useMemo(() => ({
    type: "What happened?",
    active: copy.stillActive.title,
    access: copy.unauthorizedAccess.prompt,
    taken: copy.dataTaken.prompt,
    noticed: "When did you first notice it?",
    org: "What kind of organization are you?",
    where: "Where do the affected people live?",
    data: "What data might be involved?",
    encrypted: copy.encrypted.prompt,
    demand: "Has anyone demanded money, or threatened to leak or publish data?",
    paid: "Has any of it already been paid?",
    help: "Which of these can you call today?",
  }), [copy])

  const hints: Partial<Record<ScreenId, string>> = {
    type: "Pick the closest one. You can change it later. The rest takes about two minutes, and you can skip anything.",
    active: copy.stillActive.hint,
    access: "This and the next question decide when some legal notification clocks start. \u201cI don't know\u201d is a fine answer: we'll hold those clocks and remind you to come back. " + copy.unauthorizedAccess.why,
    taken: copy.dataTaken.why,
    noticed: i.incidentType === "vendor"
      ? "If a vendor told you, use the date and time they contacted you. Some deadlines are measured from when you learned of it."
      : "An estimate is fine. Some deadlines are measured from when you discovered an incident, so this time matters.",
    org: "Pick all that apply. This only affects which rules we point you to.",
    where: "Some privacy laws depend on where the people live. You can add the states later.",
    data: "Pick all that might be on the affected systems. Guessing wide is safer than guessing narrow.",
    encrypted: copy.encrypted.why,
    demand: "A demand changes the advice: don't reply or pay before talking to your lawyer and law enforcement.",
    paid: "Payments can carry sanctions risk. Your lawyer and law enforcement need to know what was paid, when, and to whom.",
    help: "Pick all that apply. This only changes the first items in your plan. We never contact them.",
  }


  // A question counts as answered (so Next replaces Skip) once the person has picked something.
  const answered = (() => {
    const key = FIELD_FOR[screen]
    if (key) return i.touched.includes(key)
    switch (screen) {
      case "type": return i.incidentType !== null
      case "noticed": return !!noticed
      case "org": return i.orgTypes.length > 0
      case "where": return geographyOf(i) !== null
      case "data": return i.dataTypes.length > 0
      case "help": return helpOf(i) !== null
      default: return false
    }
  })()


  function finish() {
    const intake: Intake = { ...i, noticedAt: toIso(noticed), confirmedAt: toIso(confirmed) }
    let w = newWorkspace()
    w = { ...w, intake, intakeComplete: true }
    if (intake.noticedAt) w = addLog(w, "Incident first noticed (as entered during intake).", new Date(intake.noticedAt))
    w = addLog(w, "Opened the incident workspace.")
    if (!saveWorkspace(w)) {
      setError("Couldn't save in this browser (private mode or storage blocked). You can still continue, but use Export to keep your work.")
    }
    clearDraft()
    router.push(`/incident?id=${encodeURIComponent(w.id)}`)
  }

  const yn = (name: string, key: AnswerKey) => (
    <SingleChoice name={name} options={YNU} value={shown(i, key)} onChange={(v) => answer(key, v as never)} />
  )
  const field = "w-full max-w-sm rounded-md border border-border bg-background p-3 text-base"

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6" aria-live="polite">
        <div className="mb-2 flex justify-between text-sm text-muted-foreground">
          <span>Question {idx + 1} of {screens.length}</span><span>You can skip anything you don&apos;t know</span>
        </div>
        <div className="h-2 rounded-full bg-muted" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-2 rounded-full bg-accent-solid transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground print:hidden">
        <span>Your answers are saved in this browser. We never ask for names, emails, company names, or IP addresses.</span>
        <span className="flex gap-4">
          <button type="button" onClick={copyLink} className="underline hover:text-foreground">Copy a link to these answers</button>
          <button type="button" onClick={startOver} className="underline hover:text-foreground">Start over (clears your answers on this device)</button>
        </span>
      </div>
      {note && <p role="status" className="mb-3 rounded-md border border-border bg-card p-2 text-xs text-muted-foreground">{note}</p>}

      <h1 ref={headingRef} tabIndex={-1} className="mb-2 text-2xl font-bold outline-none sm:text-3xl">{titles[screen]}</h1>
      {hints[screen] && <p className="mb-4 text-muted-foreground">{hints[screen]}</p>}

      {screen === "type" && <SingleChoice name="type" options={INCIDENT_TYPES} value={i.incidentType} onChange={(v) => set("incidentType", v as IncidentTypeId)} />}
      {screen === "active" && yn("active", "stillActive")}
      {screen === "access" && yn("access", "unauthorizedAccess")}
      {screen === "taken" && yn("taken", "dataTaken")}
      {screen === "noticed" && (
        <div className="grid gap-3">
          <input type="datetime-local" aria-label="When you first noticed it" className={field} value={noticed} onChange={(e) => setNoticed(e.target.value)} />
          <div><Button variant="outline" size="sm" onClick={() => setNoticed(nowLocal())}>Use the current time</Button></div>
        </div>
      )}
      {screen === "org" && <MultiChoice options={ORG_TYPES} values={i.orgTypes} onChange={(v) => set("orgTypes", v as Intake["orgTypes"])} exclusive={["none"]} />}
      {screen === "where" && <SingleChoice name="where" options={GEOGRAPHY} value={geographyOf(i)} onChange={(v) => setI((p) => withGeography(p, v as GeographyId))} />}
      {screen === "data" && <MultiChoice options={DATA_TYPES} values={i.dataTypes} onChange={(v) => set("dataTypes", v as Intake["dataTypes"])} exclusive={["none-known"]} />}
      {screen === "encrypted" && yn("enc", "encrypted")}
      {screen === "demand" && <SingleChoice name="demand" options={DEMANDS} value={shown(i, "demand")} onChange={(v) => answer("demand", v as Demand)} />}
      {screen === "paid" && <SingleChoice name="paid" options={PAID_OPTIONS} value={shown(i, "paid")} onChange={(v) => answer("paid", v as never)} />}
      {screen === "help" && <MultiChoice options={HELP_OPTIONS} values={helpOf(i) ?? []} exclusive={["none"]} onChange={(v) => setI((p) => withHelp(p, v))} />}

      {error && <p role="alert" className="mt-4 text-sm text-destructive">{error}</p>}

      {idx >= 1 && !last && (
        <div className="mt-8 rounded-lg border border-border bg-card p-4 print:hidden">
          <Button type="button" variant="outline" size="lg" className="h-auto w-full whitespace-normal py-3 text-left sm:w-auto" onClick={finish}>
            Skip the rest of the questions and show me what to do right now
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">You can answer anything you skipped later, from your plan.</p>
        </div>
      )}

      <div className="mt-8 flex items-center justify-between gap-3">
        <Button variant="outline" size="lg" onClick={() => go(idx - 1)} disabled={idx === 0}>
          <ArrowLeft /> Back
        </Button>
        {last ? (
          <Button size="lg" className="bg-accent-solid text-accent-solid-foreground hover:bg-accent-solid/90" onClick={finish}>Finish questions <ArrowRight /></Button>
        ) : answered ? (
          <Button size="lg" className="bg-accent-solid text-accent-solid-foreground hover:bg-accent-solid/90" onClick={() => go(idx + 1)}>Next <ArrowRight /></Button>
        ) : (
          <Button size="lg" variant="outline" onClick={() => go(idx + 1)}>Skip for now <ArrowRight /></Button>
        )}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <SaveProgress save={() => { saveDraft({ intake: i, noticed, confirmed }); setSavedAt(new Date().toISOString()); return true }} savedAt={savedAt} />
        <ClearAll />
      </div>
    </div>
  )
}
